import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { Id } from "@/jenesys-types/data/common";
import { AppError } from "@/jenesys-types/errors";

interface DocumentStatusState {
  isLoading: boolean;
  uploadError?: AppError;
  status: string | null;
}

const initialState: DocumentStatusState = {
  isLoading: false,
  uploadError: undefined,
  status: null
};

interface DocumentStatusPayload {
  clientId?: Id;
}

interface DocumentStatusResponsePayload {
  status: string;
}

export type DocumentStatusActions = PayloadAction<DocumentStatusPayload>;
export type DocumentStatusResponseActions =
  PayloadAction<DocumentStatusResponsePayload>;

const documentStatusSlice = createSlice({
  name: "state/features/documentsUploadStatus",
  initialState,
  reducers: {
    getDocumentStatus: {
      reducer: (state: DocumentStatusState) => {
        state.isLoading = true;
        state.uploadError = undefined;
      },
      prepare: (clientId: Id) => ({ payload: { clientId } })
    },
    setDocumentStatus: (
      state: DocumentStatusState,
      action: DocumentStatusResponseActions
    ) => {
      state.isLoading = false;
      state.status = action.payload.status;
    },
    setDocumentStatusError: (
      state: DocumentStatusState,
      action: PayloadAction<AppError>
    ) => {
      state.isLoading = false;
      state.uploadError = action.payload;
    }
  }
});

export const { getDocumentStatus, setDocumentStatus, setDocumentStatusError } =
  documentStatusSlice.actions;

const selectDocumentStatusState = (state: {
  features: { DocumentStatus: DocumentStatusState };
}): DocumentStatusState => state.features.DocumentStatus;

export const selectIsLoading = createSelector(
  selectDocumentStatusState,
  (state) => state.isLoading
);
export const selectDocumentStatusError = createSelector(
  selectDocumentStatusState,
  (state) => state.uploadError
);
export const selectDocumentStatus = createSelector(
  selectDocumentStatusState,
  (state) => state.status
);

export default documentStatusSlice.reducer;
